import React from 'react';
import { 
    Box, 
    Typography, 
    useTheme, 
    alpha,
    CircularProgress,
    LinearProgress,
    Fade
} from '@mui/material';
import { RiShieldCheckLine, RiLoaderLine } from '@remixicon/react';

export interface LoadingProps {
    variant?: 'circular' | 'linear' | 'skeleton' | 'pulsing' | 'dots';
    size?: 'small' | 'medium' | 'large';
    message?: string;
    fullscreen?: boolean;
    overlay?: boolean;
    showIcon?: boolean;
    color?: 'primary' | 'secondary' | 'info';
    transparent?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
    variant = 'circular',
    size = 'medium',
    message = 'Cargando...',
    fullscreen = true,
    overlay = false,
    showIcon = false,
    color = 'primary',
    transparent = false
}) => {
    const theme = useTheme();

    const getSizeProps = () => {
        switch (size) {
            case 'small':
                return { 
                    spinner: 24, 
                    icon: 20, 
                    typography: 'body2',
                    spacing: 1.5 
                };
            case 'large':
                return { 
                    spinner: 64, 
                    icon: 48, 
                    typography: 'h6',
                    spacing: 3 
                };
            default:
                return { 
                    spinner: 40, 
                    icon: 32, 
                    typography: 'body1',
                    spacing: 2 
                };
        }
    };

    const sizeProps = getSizeProps();
    const themeColor = theme.palette[color];

    const renderSpinner = () => {
        switch (variant) {
            case 'linear':
                return (
                    <Box sx={{ width: '100%', maxWidth: 300 }}>
                        <LinearProgress 
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: alpha(themeColor.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    backgroundColor: themeColor.main,
                                },
                            }}
                        />
                    </Box>
                );
            
            case 'pulsing':
                return (
                    <Box
                        sx={{
                            width: sizeProps.spinner,
                            height: sizeProps.spinner,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(themeColor.main, 0.2)} 0%, ${alpha(themeColor.main, 0.05)} 70%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                background: `radial-gradient(circle, ${alpha(themeColor.main, 0.3)} 0%, transparent 70%)`,
                                animation: 'pulse 1.5s ease-in-out infinite',
                            },
                        }}
                    >
                        <RiLoaderLine 
                            size={sizeProps.icon} 
                            color={themeColor.main}
                            style={{
                                animation: 'spin 2s linear infinite',
                                zIndex: 1,
                            }}
                        />
                    </Box>
                );
            
            case 'dots':
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                        }}
                    >
                        {[0, 1, 2].map((index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: size === 'small' ? 8 : size === 'large' ? 16 : 12,
                                    height: size === 'small' ? 8 : size === 'large' ? 16 : 12,
                                    borderRadius: '50%',
                                    backgroundColor: themeColor.main,
                                    animation: 'bounce 1.4s ease-in-out infinite both',
                                    animationDelay: `${index * 0.16}s`,
                                }}
                            />
                        ))}
                        <style>
                            {`
                                @keyframes bounce {
                                    0%, 80%, 100% {
                                        transform: scale(0);
                                        opacity: 0.5;
                                    }
                                    40% {
                                        transform: scale(1);
                                        opacity: 1;
                                    }
                                }
                            `}
                        </style>
                    </Box>
                );
            
            case 'skeleton':
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            width: '100%',
                            maxWidth: 300,
                        }}
                    >
                        {[0, 1, 2].map((index) => (
                            <Box
                                key={index}
                                sx={{
                                    height: size === 'small' ? 12 : size === 'large' ? 20 : 16,
                                    backgroundColor: alpha(themeColor.main, 0.1),
                                    borderRadius: 1,
                                    width: `${100 - (index * 15)}%`,
                                    animation: 'shimmer 1.5s ease-in-out infinite',
                                    background: `linear-gradient(90deg, ${alpha(themeColor.main, 0.1)} 25%, ${alpha(themeColor.main, 0.2)} 50%, ${alpha(themeColor.main, 0.1)} 75%)`,
                                    backgroundSize: '200% 100%',
                                }}
                            />
                        ))}
                        <style>
                            {`
                                @keyframes shimmer {
                                    0% { background-position: -200% 0; }
                                    100% { background-position: 200% 0; }
                                }
                            `}
                        </style>
                    </Box>
                );
            
            default: // circular
                return (
                    <CircularProgress
                        size={sizeProps.spinner}
                        thickness={4}
                        sx={{
                            color: themeColor.main,
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                            },
                        }}
                    />
                );
        }
    };

    const renderContent = () => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: sizeProps.spacing,
                textAlign: 'center',
            }}
        >
            {showIcon && (
                <Fade in={true} timeout={500}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: sizeProps.icon + 20,
                            height: sizeProps.icon + 20,
                            borderRadius: '50%',
                            backgroundColor: alpha(themeColor.main, 0.1),
                            mb: 1,
                        }}
                    >
                        <RiShieldCheckLine 
                            size={sizeProps.icon} 
                            color={themeColor.main}
                        />
                    </Box>
                </Fade>
            )}
            
            <Fade in={true} timeout={800}>
                <Box>{renderSpinner()}</Box>
            </Fade>
            
            {message && (
                <Fade in={true} timeout={1000}>
                    <Typography
                        variant={sizeProps.typography as any}
                        sx={{
                            color: theme.palette.grey[600],
                            fontWeight: 500,
                            mt: 1,
                            maxWidth: 300,
                        }}
                    >
                        {message}
                    </Typography>
                </Fade>
            )}
        </Box>
    );

    const containerSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(fullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: transparent 
                ? 'transparent' 
                : overlay 
                    ? 'rgba(255, 255, 255, 0.9)' 
                    : 'transparent',
            backdropFilter: overlay ? 'blur(4px)' : 'none',
        }),
        ...(!fullscreen && {
            minHeight: overlay ? '100vh' : '200px',
            ...(overlay && {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: transparent 
                    ? 'transparent' 
                    : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                zIndex: 1000,
            }),
        }),
    };

    // Agregar estilos de animación al DOM
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            @keyframes pulse {
                0%, 100% { 
                    transform: scale(1); 
                    opacity: 1; 
                }
                50% { 
                    transform: scale(1.05); 
                    opacity: 0.7; 
                }
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    if (fullscreen || overlay) {
        return (
            <Fade in={true} timeout={300}>
                <Box sx={containerSx}>
                    {renderContent()}
                </Box>
            </Fade>
        );
    }

    return (
        <Box sx={containerSx}>
            {renderContent()}
        </Box>
    );
};

export default Loading;
